import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";

import {
    LgLocalizationModule,
    LgTranslateService,
    useTranslationNamespace,
} from "@logex/framework/lg-localization";
import { UiCoreModule } from "@logex/framework/ui-core";

import { UniversalDialog } from "@codman/shared/ui-universal-dialog";
import {
    AllowedBenchmarks,
    Benchmark,
    FeatureBenchmarkingService,
    IBenchmarkDimension,
    IComparisonType,
    IConnectBenchmark,
    IConnectProvider,
    IMultiDataSelection,
    IPatientsCount,
    IPatientsCountMultiData,
    Provider,
} from "@codman/shared/feature-benchmarking";
import { SharedRegionsPipe } from "@codman/shared/util-pipes";

import { SharedComparisonPickerButtonEditComponent } from "../comparison-picker-edit/comparison-picker-button-edit/comparison-picker-button-edit.component";
import { SharedComparisonPickerButtonInfoComponent } from "../comparison-picker-info/comparison-picker-button-info/comparison-picker-button-info.component";
import { ComparisonPopupEditClickEvent } from "../comparison-picker-edit/comparison-picker-popup-edit/comparison-picker-popup-edit.component";
import { ComparisonPopupInfoClickEvent } from "../comparison-picker-info/comparison-picker-popup-info/comparison-picker-popup-info.component";

export interface IComparisonPickerSource {
    type: Provider;
    numerator?: number;
    denominator?: number;
    text?: string;
    fullText?: string;
    analysisUnit?: string;
    dimension?: string;
}

export const minAllowedDataSourceCount = 2;
export const maxAllowedDataSourceCount = 3;

@Component({
    selector: "codman-comparison-picker",
    templateUrl: "./comparison-picker.component.html",
    styleUrls: ["./comparison-picker.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        LgLocalizationModule,
        UiCoreModule,
        SharedComparisonPickerButtonEditComponent,
        SharedComparisonPickerButtonInfoComponent,
    ],
    providers: [UniversalDialog, useTranslationNamespace("APP._Shared.ComparisonPicker")],
})
export class SharedComparisonPickerComponent implements OnInit, OnChanges {
    protected _benchmarkingService = inject(FeatureBenchmarkingService);
    _translationService = inject(LgTranslateService);
    private _regionsPipe = inject(SharedRegionsPipe);

    @Input() allowedBenchmarks: AllowedBenchmarks = {};
    @Input() rightSidebarPinned: boolean = false;
    @Input() selectedOrganizationName: string | null = null;
    @Input() selectedConnectProviders: number[] | null | undefined = [];
    @Input() patientsCount: IPatientsCount | IPatientsCountMultiData | null | undefined;
    @Input() analysisUnit = "";
    @Input() availableConnectProviders: IConnectProvider[] | null = [];
    @Input() selectedBenchmark: Benchmark | null = null;
    @Input() selectedProviders: Provider[] | null = [];
    @Input() selectedProvidersMultiData: IMultiDataSelection | null | undefined = null;
    @Input() benchmarkDimension: IBenchmarkDimension | null | undefined = null;
    @Input() comparisonType: IComparisonType | null = null;
    @Input() regions?: string[] | null;

    @Output() readonly handlePickerInfoEvent = new EventEmitter<ComparisonPopupInfoClickEvent>();

    _allSources: IComparisonPickerSource[] = [];
    _comparisonPickerSources$: Observable<IComparisonPickerSource[]>;
    _patientsCount$: BehaviorSubject<IPatientsCount | IPatientsCountMultiData | {}> =
        new BehaviorSubject({});
    _availableConnectProviders$: BehaviorSubject<IConnectProvider[]> = new BehaviorSubject<
        IConnectProvider[]
    >([]);
    _selectedBenchmark$: BehaviorSubject<Benchmark> = new BehaviorSubject<Benchmark>(
        {} as Benchmark,
    );
    _selectedProviders$: BehaviorSubject<Provider[]> = new BehaviorSubject<Provider[]>([]);
    _selectedProvidersMultiData$: BehaviorSubject<IMultiDataSelection | null> =
        new BehaviorSubject<IMultiDataSelection | null>(null);
    _benchmarkDimension$: BehaviorSubject<IBenchmarkDimension | undefined> = new BehaviorSubject<
        IBenchmarkDimension | undefined
    >(undefined);
    _comparisonType$: BehaviorSubject<IComparisonType> = new BehaviorSubject<IComparisonType>(
        "basic",
    );

    constructor() {
        this._comparisonPickerSources$ = combineLatest([
            this._selectedProviders$,
            this._selectedProvidersMultiData$,
            this._comparisonType$,
            this._patientsCount$,
        ]).pipe(
            map(
                ([
                    selectedProviders,
                    selectedProvidersMultiData,
                    comparisonType,
                    patientsCount,
                ]): IComparisonPickerSource[] => {
                    const sources: IComparisonPickerSource[] = [];

                    if (comparisonType === "multiData") {
                        selectedProvidersMultiData?.dimensions.forEach((dimension, index) => {
                            sources.push(
                                this._getProviderDetails(
                                    selectedProvidersMultiData.provider,
                                    comparisonType,
                                    patientsCount,
                                    dimension,
                                    index,
                                ),
                            );
                        });
                    } else {
                        selectedProviders.forEach(provider => {
                            sources.push(
                                this._getProviderDetails(provider, comparisonType, patientsCount),
                            );
                        });
                    }

                    return sources;
                },
            ),
        );
    }

    ngOnInit(): void {
        this._updateAllSources();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.selectedOrganizationName ||
            changes.selectedConnectProviders ||
            changes.patientsCount ||
            changes.allowedBenchmarks
        ) {
            this._updateAllSources();
        }

        if (changes.patientsCount && this.patientsCount) {
            this._patientsCount$.next(this.patientsCount);
        }
        if (changes.availableConnectProviders && this.availableConnectProviders) {
            this._availableConnectProviders$.next(this.availableConnectProviders);
        }
        if (changes.selectedBenchmark && this.selectedBenchmark) {
            this._selectedBenchmark$.next(this.selectedBenchmark);
        }
        if (changes.selectedProviders && this.selectedProviders) {
            this._selectedProviders$.next(this.selectedProviders);
        }
        if (changes.selectedProvidersMultiData && this.selectedProvidersMultiData) {
            this._selectedProvidersMultiData$.next(this.selectedProvidersMultiData);
        }
        if (changes.benchmarkDimension && this.benchmarkDimension) {
            this._benchmarkDimension$.next(this.benchmarkDimension);
        }
        if (changes.comparisonType && this.comparisonType) {
            this._comparisonType$.next(this.comparisonType);
        }
    }

    private _updateAllSources(): void {
        this._allSources = [this._getProviderDetails("ownProvider", "basic", this.patientsCount)];
        if (this.allowedBenchmarks.regional) {
            this._allSources.push(
                this._getProviderDetails("nationalBenchmark", "basic", this.patientsCount),
            );
        }
        if (this.allowedBenchmarks.connect) {
            this._allSources.push(
                this._getProviderDetails("connectBenchmark", "basic", this.patientsCount),
            );
        }
    }

    _getProviderDetails(
        provider: Provider,
        comparisonType: IComparisonType = "basic",
        patientsCount?: IPatientsCount | IPatientsCountMultiData | null | {},
        dimension?: string,
        index?: number,
    ): IComparisonPickerSource {
        let numerator: number | undefined;
        let denominator: number | undefined;

        if (
            comparisonType === "multiData" &&
            index !== undefined &&
            (patientsCount as IPatientsCountMultiData)?.multiData?.length > index
        ) {
            numerator = (patientsCount as IPatientsCountMultiData).multiData[index]?.numerator;
            denominator = (patientsCount as IPatientsCountMultiData).multiData[index]?.denominator;
        } else {
            if (provider === "nationalBenchmark") {
                numerator = (patientsCount as IPatientsCount)?.nationalBenchmarkNumerator;
                denominator = (patientsCount as IPatientsCount)?.nationalBenchmarkDenominator;
            } else if (provider === "connectBenchmark") {
                numerator = (patientsCount as IPatientsCount)?.connectBenchmarkNumerator;
                denominator = (patientsCount as IPatientsCount)?.connectBenchmarkDenominator;
            } else {
                numerator = (patientsCount as IPatientsCount)?.numerator;
                denominator = (patientsCount as IPatientsCount)?.denominator;
            }
        }

        return {
            type: provider,
            numerator,
            denominator,
            analysisUnit: this.analysisUnit,
            text: this._getButtonText(provider, false, dimension),
            fullText: this._getButtonText(provider, true, dimension),
            dimension,
        };
    }

    _shortenText(text: string): string {
        return text.length > 20 ? text.substring(0, 20) + "..." : text;
    }

    _getButtonText(provider: Provider, fullText: boolean, dimension?: string): string {
        const dimensionText = dimension
            ? fullText
                ? dimension
                : this._shortenText(dimension)
            : undefined;
        if (dimensionText) return dimensionText;
        switch (provider) {
            case "nationalBenchmark":
                return this._regionsPipe.transform(
                    this._translationService.translate("APP.NationalBenchmark"),
                    this.regions,
                );
            case "connectBenchmark":
                const count =
                    this.comparisonType === "multiData"
                        ? (this.selectedProvidersMultiData?.connectProviders.length ?? 0)
                        : (this.selectedConnectProviders?.length ?? 0);
                return (
                    count +
                    " " +
                    this._translationService.translate(
                        "APP._Shared.BenchmarkSelector.Benchmarks.Connect." +
                            (count === 1 ? "Provider" : "Providers"),
                    )
                );
            default:
            case "ownProvider":
                return (
                    this.selectedOrganizationName ??
                    this._translationService.translate(
                        "APP._Shared.BenchmarkSelector.Benchmarks.OwnProvider.ShortLabel",
                    )
                );
        }
    }

    _handlePickerEditEvent(event: ComparisonPopupEditClickEvent, benchmark: Benchmark): void {
        if (event.type === "update") {
            this._benchmarkingService.updateSelectedProviders(event.selectedProviders, false);
        } else if (event.type === "reset") {
            this._benchmarkingService.resetSelectedProviders();
        } else if (event.type === "connect") {
            this._benchmarkingService.updateSelectedBenchmark(
                {
                    type: "connect",
                    connectProviders: event.connectProviders,
                    regions: (benchmark as IConnectBenchmark).regions,
                },
                event.multiData,
            );
            this._benchmarkingService.updateSelectedProviders(
                [],
                true,
                event.connectProviders,
                false,
            );
            this._benchmarkingService.filterReset$.next("treatmentName");
        } else if (event.type === "updateMultiData") {
            this._benchmarkingService.updateSelectedProvidersMultiData({
                provider: event.selectedProvider,
                dimensions: event.selectedDimensions,
                connectProviders: event.connectProviders ?? [],
            });
        }
    }

    _handlePickerInfoEvent(event: ComparisonPopupInfoClickEvent, benchmark: Benchmark): void {
        if (event.type === "trend" || event.type === "list") {
            this.handlePickerInfoEvent.emit(event);
        } else if (event.type === "connect") {
            this._benchmarkingService.updateSelectedBenchmark(
                {
                    type: "connect",
                    connectProviders: event.connectProviders,
                    regions: (benchmark as IConnectBenchmark).regions,
                },
                event.multiData,
            );
            this._benchmarkingService.updateSelectedProviders(
                [],
                true,
                event.connectProviders,
                false,
            );
        }
    }

    _removeProvider(provider: Provider): void {
        this._benchmarkingService.removeProvider(provider);
    }

    _removeMultiDataDimension(dimension?: string): void {
        if (dimension) this._benchmarkingService.removeMultiDataDimension(dimension);
    }
}
